import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X } from "lucide-react";
import { useAppContext } from "../context/AppContext"; // ✅ adjust path if needed
import { Link } from "react-router-dom";

export default function SearchModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [filtered, setFiltered] = useState([]);
  const inputRef = useRef(null);

  // ✅ Get truck data from context
  const { trucks } = useAppContext();

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 150);
    }
  }, [isOpen]);

  // 🔍 Live filtering
  useEffect(() => {
    if (!query.trim()) {
      setFiltered([]);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const results = trucks.filter(
      (truck) =>
        truck.name.toLowerCase().includes(lowerQuery) ||
        truck.series?.toLowerCase().includes(lowerQuery) ||
        truck.category?.toLowerCase().includes(lowerQuery)
    );
    setFiltered(results);
  }, [query, trucks]);

  return (
    <>
      {/* 🔍 Search Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="ml-2 p-2 rounded-full hover:bg-gray-100 transition"
        title="Search"
      >
        <Search size={20} className="text-gray-800 hover:text-[#E62E2D]" />
      </button>

      {/* 🔥 Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-[1000] flex items-center justify-center backdrop-blur-lg bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ y: 50, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ type: "spring", stiffness: 100, damping: 15 }}
              className="relative bg-gradient-to-b from-[#111] via-[#1a1a1a] to-[#111] border border-[#E62E2D]/50 rounded-2xl shadow-[0_0_25px_rgba(230,46,45,0.4)] w-[90%] md:w-[600px] p-6 text-white"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Input Field */}
              <div className="flex items-center space-x-3">
                <Search size={22} className="text-[#E62E2D]" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search trucks, models, or series..."
                  className="flex-1 bg-transparent border-none outline-none text-lg placeholder-gray-400 text-white"
                />
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-full hover:bg-[#E62E2D]/20 transition"
                >
                  <X size={22} className="text-gray-300 hover:text-[#E62E2D]" />
                </button>
              </div>

              {/* Results */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mt-5 border-t border-[#E62E2D]/20 pt-4 text-sm text-gray-300 max-h-[300px] overflow-y-auto"
              >
                {!query ? (
                  <p className="italic text-gray-400">
                    Start typing to search models...
                  </p>
                ) : filtered.length === 0 ? (
                  <p className="text-gray-400">
                    No results found for{" "}
                    <span className="text-[#E62E2D] font-semibold">
                      "{query}"
                    </span>
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {filtered.map((truck) => (
                      <li key={truck.id}>
                        <Link
                          to={`/trucks/${truck.slug}`}
                          onClick={() => setIsOpen(false)}
                          className="block p-3 rounded-lg hover:bg-[#E62E2D]/20 cursor-pointer transition group"
                        >
                          <p className="font-semibold text-white group-hover:text-[#E62E2D]">
                            {truck.name}
                          </p>
                          <p className="text-xs text-gray-400">
                            {truck.series} — {truck.category}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
